/**
 * Lightweight OpenTelemetry Provider
 *
 * This provider is intentionally minimal to avoid conflicts with Sentry:
 * - No auto-instrumentation (Sentry handles that)
 * - Manual span creation only (via withTrace)
 *
 * Benefits:
 * - Full control over span creation
 * - No performance overhead from auto-instrumentation
 * - No conflicts with Sentry's tracing
 * - Spans created only for explicit user operations
 */
import { trace } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

import { OTEL_COLLECTOR_URL } from '@constants/config';
import { OTEL_SERVICE_NAME } from '@constants/otel';

/**
 * Initialize OpenTelemetry with minimal configuration.
 * This only sets up the tracer provider - NO auto-instrumentation.
 */
const initializeOTel = (): void => {
  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: OTEL_SERVICE_NAME,
  });

  // If an OTel collector URL is configured, add an OTLP exporter and batch span processor
  let processor = undefined;
  if (OTEL_COLLECTOR_URL) {
    const exporter = new OTLPTraceExporter({ url: OTEL_COLLECTOR_URL });
    processor = new BatchSpanProcessor(exporter);
  }

  const provider = new WebTracerProvider({
    resource: resource,
    spanProcessors: processor ? [processor] : [],
    // No sampler configured here (uses SDK default) - we control spans manually via withTrace
  });

  // Register the provider globally so we can create spans manually
  provider.register();
};

/**
 * Get the tracer for manual span creation.
 * This is used by withTrace to create spans with custom names.
 */
export const getTracer = () => trace.getTracer(OTEL_SERVICE_NAME);

// Initialize OTel on module load (safe, no side effects)
initializeOTel();
